import angular from "angular";
import type { TokenService } from './services/authentication/TokenService';
import type { AccountService } from './services/account/AccountService';
import type { IConfigService } from './services/config/ConfigService';
import type { StateService } from './services/state/StateService';
import type { TrackService } from './services/auditing/TrackService';
import type { IHttpWrapperService } from './services/http';
import { getActiveMessages } from '../src/services/systemMessages';

interface IAuthService {
  append(config, deferred): void;

  loginConfirmed(): void;
}

class AuthService implements angular.IServiceProvider {
    /**
     * Holds all the requests which failed due to 401 response,
     * so they can be re-requested in future, once login is completed.
     */
    private buffer = [];

    /**
     * Required by HTTP interceptor.
     * Function is attached to provider to be invisible for regular users of this service.
     */
    private pushToBuffer = (config, deferred) => {
      this.buffer.push({
        config: config,
        deferred: deferred
      });
    };

    public $get = ['$rootScope', '$injector', ($rootScope, $injector) => {
      let $http; //initialized later because of circular dependency problem
      return {
        append: (config, deferred) => {
          this.buffer.push({
            config: config,
            deferred: deferred
          });
        },
        loginConfirmed: () => {

          $rootScope.$broadcast('event:auth-loginConfirmed');
          for (let i = 0; i < this.buffer.length; ++i) {
            $http = $http || $injector.get('$http');
            let deferred = this.buffer[i].deferred;
            $http(this.buffer[i].config).then((response) => {
              deferred.resolve(response);
            });
          }
          this.buffer = [];
        }
      }
    }]
}

// Configure AuthService provider
(window as any).frontendApp.provider('authService', new AuthService());

(window as any).frontendApp

  .config(['$routeProvider', '$locationProvider', '$httpProvider', '$uibTooltipProvider', '$animateProvider', ($routeProvider, $locationProvider, $httpProvider, $uibTooltipProvider, $animateProvider) => {

    $animateProvider.classNameFilter(/angular-animate/);

    $routeProvider
      .when('/', {templateUrl: 'views/home.html', controller: 'HomeController'})
      .when('/verify/:email/:accountId/:passwordResetToken', { template: '<react-reset-password-page></react-reset-password-page>' })
      .when('/reset/:email/:accountId/:passwordResetToken', { template: '<react-reset-password-page></react-reset-password-page>' })
      .when('/settings', { template: '<react-settings-page></react-settings-page>' })
      .when('/project', { template: '<react-project-list-page></react-project-list-page>' })
      .when('/project/:projectId', { template: '<react-project-detail-page></react-project-detail-page>' })
      .when('/project/:projectId/collections', { template: '<react-project-detail-page></react-project-detail-page>' })
      .when('/project/:projectId/getstarted', { template: '<react-get-started-page></react-get-started-page>' })
      .when('/project/:projectId/admin', { template: '<react-project-admin-page></react-project-admin-page>' })
      .when('/project/:projectId/collection/:collectionId', {
        templateUrl: 'views/collection.html',
        controller: 'CollectionController',
        reloadOnSearch: false
      })
      .when('/project/:projectId/search', {
        templateUrl: 'views/search_results.html',
        controller: 'SearchController'
      })
      .when('/project/:projectId/browse', {
        templateUrl: 'views/project_browse.html',
        controller: 'BrowseController',
        reloadOnSearch: false
      })
      .when('/project/:projectId/collection/:collectionId/search', {
        templateUrl: 'views/search_results.html',
        controller: 'SearchController'
      })
      .when('/project/:projectId/home/batch/:batchId', {
        templateUrl: 'views/home_results.html',
        controller: 'BrowseController',
        action: "simplified_review",
        reloadOnSearch: false
      })
      .when('/project/:projectId/home/batch/:batchId/:reviewState', {
        templateUrl: 'views/home_results.html',
        controller: 'BrowseController',
        action: "simplified_review",
        reloadOnSearch: false
      })
      .when('/project/:projectId/home/search/:searchId', {
        templateUrl: 'views/home_results.html',
        controller: 'SearchController',
        action: "simplified_review",
        reloadOnSearch: false
      })
      .when('/project/:projectId/loadfile', {templateUrl: 'views/load_file.html', controller: 'LoadFileCtrl'})
      .when('/organization', { template: '<react-organization-page></react-organization-page>' })
      .when('/notification', {templateUrl: 'views/notification.html', controller: 'NotificationCtrl'})
      .when('/pagenotfound', { template: '<react-not-found-page></react-not-found-page>' })
      .otherwise({redirectTo: '/pagenotfound'});

    $locationProvider.hashPrefix('!');

    $uibTooltipProvider.setTriggers({'iioPopoverOpen': 'iioPopoverClose'});

    $httpProvider.interceptors.push('myHttpInterceptor');
    $httpProvider.interceptors.push('AuthInterceptor');

    // CORS stuff
    $httpProvider.defaults.useXDomain = true;
    delete $httpProvider.defaults.headers.common['X-Requested-With'];
    $httpProvider.defaults.headers.delete = {'Content-Type': 'application/json;charset=utf-8'};
    $httpProvider.defaults.headers.get = {'Content-Type': 'application/json;charset=utf-8'};

    //$httpProvider.defaults.headers.delete = {'Content-Type': 'application/json;charset=utf-8'};
    $httpProvider.defaults.timeout = 120000;

  }])

  .run(['$rootScope', '$location', '$window', '$log', 'TokenService', 'AccountService', 'ConfigService', 'StateService', 'WebSocketService', 'ThemeService', 'TrackService', 'AuthorizationRefreshService', 'HttpService', ($rootScope, $location, $window, $log, tokenService: TokenService, accountService: AccountService, configService: IConfigService, stateService: StateService, webSocketService, themeService, trackService: TrackService, authorizationRefreshService, httpService: IHttpWrapperService) => {

    // Deploy fingerprint: readable in any browser via window.__BUILD_SHA__.
    $window.__BUILD_SHA__ = __BUILD_SHA__;
    $log.info(`[build] v${__APP_VERSION__} sha=${__BUILD_SHA__}`);

    configService
      .setAppVersion(__APP_VERSION__)
      .setServerBaseUrl(import.meta.env.VITE_API_URL || 'http://localhost:8080/iio')
      .setFrontendUrl($window.location.protocol + '//' + $window.location.hostname + ($window.location.port ? ':' + $window.location.port : ''))
      .setEnvironment(import.meta.env.VITE_ENVIRONMENT || 'dev');

    if (import.meta.env.VITE_STRIPE_KEY) {
      configService.setStripePubKey(import.meta.env.VITE_STRIPE_KEY);
    }

    stateService.SetLoading();

    console.log('CONFIG: App Version: ' + configService.getAppVersion());
    console.log('CONFIG: Server Base Url: ' + configService.getServerBaseUrl());
    console.log('CONFIG: Server Base Url For Resource: ' + configService.getServerBaseUrlForResource());
    console.log('CONFIG: Frontend Url: ' + configService.getFrontendUrl());

    themeService.findThemeForSubdomain();

    $rootScope.features = (window as any).__features || {};
    window.addEventListener('featuresLoaded', function(e: any) {
      $rootScope.$apply(function() { $rootScope.features = e.detail; });
    }, { once: true });

    $rootScope.systemMessages = getActiveMessages();

    if (tokenService.Exists()) {

      // load our account
      console.log('Requesting details for account');
      accountService.Fetch();

      console.log('Account: ', accountService.Get());
      console.log('Token: ', tokenService.Get());

      // After successfully get the account info
      stateService.SetLoggedIn();

      // Logged-in users landing on the home/login page should go to /project
      if ($location.path() === '/') {
        $location.path('/project');
      }

    } else {
      tokenService.Clear();
      stateService.SetLoggedOut();

      if ($location.path() && !$location.path().startsWith('/reset/') && !$location.path().startsWith('/verify/')) {
        $location.path('/');
      }
    }

    // Route-level auth guards — enforced on every navigation, not just initial load.
    const publicPaths = ['/', '/pagenotfound'];
    const isPublicPath = (path: string) =>
      publicPaths.includes(path) ||
      path.startsWith('/reset/') ||
      path.startsWith('/verify/');

    $rootScope.$on('$routeChangeStart', (_event, next) => {
      const path = next && next.$$route && next.$$route.originalPath || $location.path();
      if (stateService.IsLoggedIn() && path === '/') {
        $location.path('/project');
      } else if (stateService.IsLoggedOut() && !stateService.IsLoading() && !isPublicPath(path)) {
        $location.path('/');
      }
    });

    $rootScope.$on('$routeChangeSuccess', () => {
      trackService.TrackPageView($location.path());
    });
  }]);
